import React, { useCallback, useEffect, useState } from 'react';
import { HERO_CAROUSEL_SLIDES } from '../../data/heroCarouselSlides';

const AUTO_ADVANCE_MS = 6000;

export default function StoreHero({ onShopClick }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const len = HERO_CAROUSEL_SLIDES.length;

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % len);
  }, [len]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + len) % len);
  }, [len]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(goNext, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, goNext]);

  const slide = HERO_CAROUSEL_SLIDES[index];

  return (
    <section
      className="relative overflow-hidden rounded-xl shadow-xl ring-1 ring-slate-200/80 sm:rounded-2xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
      aria-roledescription="carousel"
      aria-label="Featured promotions"
    >
      <h1 className="sr-only">Tech & connectivity store</h1>
      <div className="relative aspect-[4/3] min-h-[230px] w-full sm:aspect-[2.2/1] sm:min-h-[280px] lg:max-h-[440px]">
        {HERO_CAROUSEL_SLIDES.map((s, i) => (
          <div
            key={s.src}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === index ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
            }`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${len}`}
            aria-hidden={i !== index}
          >
            <img
              src={s.src}
              alt={s.alt}
              className="h-full w-full object-cover object-center"
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/65 to-slate-900/25"
              aria-hidden
            />
            <div className="absolute inset-0 flex max-w-[86%] flex-col justify-center px-5 py-7 sm:max-w-none sm:px-10 sm:py-10 lg:max-w-[55%]">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-400 sm:text-sm">
                {s.kicker}
              </p>
              <h2 className="mt-2 text-xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                {s.title}
              </h2>
              <p className="mt-2 max-w-lg text-xs leading-relaxed text-slate-200 sm:mt-3 sm:text-base">
                {s.subtitle}
              </p>
              <div className="mt-4 sm:mt-6">
                <button
                  type="button"
                  onClick={onShopClick}
                  className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-lg transition hover:bg-amber-400 min-touch sm:min-h-0 sm:min-w-0 sm:px-5 sm:py-3"
                >
                  Shop featured
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex items-center justify-between gap-2 bg-gradient-to-t from-slate-950/80 to-transparent px-3 pb-3 pt-12 sm:px-4 sm:pb-4">
          <div className="pointer-events-auto flex flex-1 items-center justify-center gap-1.5 sm:gap-2">
            {HERO_CAROUSEL_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                className="flex min-h-11 min-w-11 items-center justify-center touch-manipulation sm:min-h-0 sm:min-w-0"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
              >
                <span
                  className={`block h-2 rounded-full transition-all ${
                    i === index ? 'w-8 bg-amber-400 sm:w-10' : 'w-2 bg-white/45 hover:bg-white/75'
                  }`}
                  aria-hidden
                />
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={goPrev}
          className="absolute left-1 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 bg-slate-900/55 p-2.5 text-white shadow-md backdrop-blur-sm transition hover:bg-slate-900/80 sm:left-3 min-touch sm:min-h-0 sm:min-w-0 sm:p-2"
          aria-label="Previous slide"
        >
          <span className="block text-lg leading-none" aria-hidden>
            ‹
          </span>
        </button>
        <button
          type="button"
          onClick={goNext}
          className="absolute right-1 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 bg-slate-900/55 p-2.5 text-white shadow-md backdrop-blur-sm transition hover:bg-slate-900/80 sm:right-3 min-touch sm:min-h-0 sm:min-w-0 sm:p-2"
          aria-label="Next slide"
        >
          <span className="block text-lg leading-none" aria-hidden>
            ›
          </span>
        </button>
      </div>

      <span className="sr-only" aria-live="polite">
        Slide {index + 1} of {len}: {slide?.title}
      </span>
    </section>
  );
}
